// Pricing migration 17 (ADO-2248): the price quotes batch-read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for row 16's
// endpoint (ADO-2247),
// `GET /api/v1/price-quotes?entry=<kind>:<depotCode>&skus=<csv>[&customerAccountCode=<code>]`.
// Modelled on `availability.client.ts`: requests are chunked to the server cap, each
// chunk succeeds or fails on its own, and the read never rejects.
//
// LOCK-STEP: the wire types below are a copy of order-capture's `PriceQuoteDto`
// (`price-quote.dto.ts`), and the request follows its validation (`price-quotes.validation.ts`,
// the account rule in `basket-items.validation.ts`); checked against both on 2026-10-23, the
// unpriced reasons again on 2026-10-29 (ADO-3988 added `PRODUCT_NOT_IN_PRICING_SCOPE`), and the
// priced fields again on 2026-11-03 (ADO-4033 added `wasRrp`). This client is the only file that
// knows the request and response shape, so a contract change stops here.
//
// Story 11.10 (ADO-1961), D-3: `margin` is on every entry when the store sees its margin and on
// none when it does not; a malformed one is left out.
//
// Shown today: `unitPrice`, `deposit`, `rrp`, `margin`, and row 18's (explain the price, ADO-2249)
// `wasPrice`, `promotionEndDate` and `kind`; and `wasRrp`, struck before `rrp` (Pricing
// migration 37, ADO-4034). `basis` is kept on the wire type but shown nowhere.

import { isMoneyValue, type MoneyValue } from '../shared/money';
import { withEntry } from './catalogue-entry';
import { API_PREFIX, apiFetch } from './shoplink-api';

/** What a screen calls the price (the pricing interface's `PriceKind`). */
export type PriceKind = 'promotion' | 'contract' | 'wow' | 'standard';

/** The pricing rule that produced the price (the pricing interface's `PriceBasis`). */
export type PriceBasis =
  | 'promotion'
  | 'contract'
  | 'wow'
  | 'band'
  | 'cost-price'
  | 'basic-price'
  | 'catalogue-price';

/** Why a product has no price: the pricing interface's reasons plus the endpoint's own two.
 *  Every one of them is the same "unpriced" state wherever the product is drawn; the reason
 *  decides only whether a list leaves it out (ADO-3948, `HIDDEN_UNPRICED_REASONS` in
 *  `pricing/listVisibility.ts`). `NOT_IN_PRICING_SCOPE` is the store's own pricing setup missing,
 *  and stays on screen; `PRODUCT_NOT_IN_PRICING_SCOPE` (ADO-3988) is a product the store's
 *  pricing does not hold, and lists hide it. */
export type UnpricedReason =
  | 'NOT_IN_PRICING_SCOPE'
  | 'PRODUCT_NOT_IN_PRICING_SCOPE'
  | 'NO_PRICE'
  | 'ZERO_PRICE'
  | 'PRICE_NOT_REPRESENTABLE'
  | 'DEPOSIT_UNAVAILABLE'
  | 'ACCOUNT_UNRESOLVED'
  | 'RANGE_UNRESOLVED'
  /** Web only, never on the wire: the endpoint refused the entry with a 403. The tech lead's
   *  answer for the designer's states (ADO-2248 PS-5): "either way the web says 'price not
   *  available'", so it is an answer, "couldn't be priced", not a failed request. */
  | 'ENTRY_FORBIDDEN';

/** Why a margin cannot be computed: pricing's reasons (lock-step with
 *  `RetailMarginUnavailableReason`). */
export type RetailMarginUnavailableReason =
  | 'NO_RRP'
  | 'NO_VAT_RATE'
  | 'NO_RETAIL_UNITS'
  | 'OUT_OF_RANGE';

const RETAIL_MARGIN_UNAVAILABLE_REASONS: ReadonlySet<string> =
  new Set<RetailMarginUnavailableReason>([
    'NO_RRP',
    'NO_VAT_RATE',
    'NO_RETAIL_UNITS',
    'OUT_OF_RANGE',
  ]);

/** The store's retail margin (lock-step with `RetailMarginDto`): `percent` is 2-decimal text,
 *  signed when negative; `unavailable` when it cannot be computed, never a zero. `reason` comes
 *  only on a priced quote's unavailable margin from the price-quotes read (Story 11.10's second
 *  PR, MI-3); the PDP's margin info reads it, and nothing else does. */
export type RetailMarginDto =
  | { readonly status: 'available'; readonly percent: string }
  | { readonly status: 'unavailable'; readonly reason?: RetailMarginUnavailableReason };

/** A priced product (lock-step with `PricedQuoteDto`). An absent figure is left out,
 *  never sent as `0` or `null`. */
export interface PricedQuoteDto {
  readonly skuCode: string;
  readonly status: 'priced';
  /** Above zero, whole cents, never includes the deposit. */
  readonly unitPrice: MoneyValue;
  /** Per unit; `0.00` when none applies. */
  readonly deposit: MoneyValue;
  readonly rrp?: MoneyValue;
  readonly wasPrice?: MoneyValue;
  /** The RSP before the promotion, struck before `rrp`; only beside `wasPrice` (ADO-4033). */
  readonly wasRrp?: MoneyValue;
  /** `YYYY-MM-DD`. */
  readonly promotionEndDate?: string;
  /** Present only when the store sees its margin (ADO-1961, D-3). */
  readonly margin?: RetailMarginDto;
  readonly kind: PriceKind;
  readonly basis: PriceBasis;
}

/** A product with no price (lock-step with `UnpricedQuoteDto`). */
export interface UnpricedQuoteDto {
  readonly skuCode: string;
  readonly status: 'unpriced';
  readonly reason: UnpricedReason;
  /** Present, always `unavailable`, only when the store sees its margin (ADO-1961, D-3). */
  readonly margin?: { readonly status: 'unavailable' };
}

export type PriceQuoteDto = PricedQuoteDto | UnpricedQuoteDto;

export const PRICE_QUOTES_PATH = `${API_PREFIX}/price-quotes`;

/** Row 16's cap on distinct SKUs per request (lock-step): the client chunks larger reads
 *  so none can 400 on size. */
export const MAX_PRICE_QUOTE_BATCH_SIZE = 100;

/**
 * The outcome of one batch read. Like availability's, it is per chunk and can be partial:
 * `quotes` carries an entry for every SKU a successful chunk answered, priced or unpriced,
 * and `unreadSkus` names the SKUs nobody answered (a failed chunk, a malformed entry, or a
 * SKU the answer left out). The two stay apart because "unpriced" is an answer and "unread"
 * is not: the screens show the failed state for the second.
 */
export interface PriceQuoteReadResult {
  readonly quotes: readonly PriceQuoteDto[];
  readonly unreadSkus: readonly string[];
}

/** The `customerAccountCode` query parameter (ADO-2248 DEC-10), or `''` when there is no
 *  account to name. Row 16 answers a blank or repeated value with a 400, so a blank one is
 *  left out and the parameter is never written twice. */
function accountParam(customerAccountCode: string | null): string {
  const code = customerAccountCode?.trim() ?? '';
  return code === '' ? '' : `&customerAccountCode=${encodeURIComponent(code)}`;
}

/** A Money value that is a usable price figure: well formed and above zero. A zero or
 *  negative figure is never shown (the contract never sends one; this keeps it so). */
function isShowableMoney(value: unknown): value is MoneyValue {
  return isMoneyValue(value) && value.amount > 0;
}

/** A margin as the wire sends it, or `null` when absent or malformed (ADO-1961). An
 *  unavailable margin's reason the web does not know, or a malformed one, is left out: the margin
 *  stays readable, with no reason (MI-3). */
export function parseRetailMargin(value: unknown): RetailMarginDto | null {
  if (typeof value !== 'object' || value === null) return null;
  const { status, percent, reason } = value as Record<string, unknown>;
  if (status === 'unavailable') {
    return typeof reason === 'string' && RETAIL_MARGIN_UNAVAILABLE_REASONS.has(reason)
      ? { status, reason: reason as RetailMarginUnavailableReason }
      : { status };
  }
  if (status === 'available' && typeof percent === 'string' && /^-?\d+\.\d{2}$/.test(percent)) {
    return { status, percent };
  }
  return null;
}

/** Parse one wire entry, or `null` when it cannot be used. A malformed optional figure
 *  (RRP, was price, was RRP, promotion end date) is left out and the entry stays priced; a malformed
 *  required field makes the whole entry unusable, so its SKU is reported unread. */
function parseQuote(value: unknown): PriceQuoteDto | null {
  if (typeof value !== 'object' || value === null) return null;
  const entry = value as Record<string, unknown>;
  const { skuCode, status } = entry;
  if (typeof skuCode !== 'string' || skuCode === '') return null;
  if (status === 'unpriced') {
    if (typeof entry['reason'] !== 'string') return null;
    const margin = parseRetailMargin(entry['margin']);
    return {
      skuCode,
      status,
      reason: entry['reason'] as UnpricedReason,
      ...(margin?.status === 'unavailable' ? { margin } : {}),
    };
  }
  if (status !== 'priced') return null;
  const { unitPrice, deposit, rrp, wasPrice, wasRrp, promotionEndDate, kind, basis } = entry;
  const margin = parseRetailMargin(entry['margin']);
  if (!isShowableMoney(unitPrice) || !isMoneyValue(deposit)) return null;
  if (typeof kind !== 'string' || typeof basis !== 'string') return null;
  return {
    skuCode,
    status,
    unitPrice: { amount: unitPrice.amount, currency: unitPrice.currency },
    deposit: { amount: deposit.amount, currency: deposit.currency },
    ...(isShowableMoney(rrp) ? { rrp: { amount: rrp.amount, currency: rrp.currency } } : {}),
    ...(isShowableMoney(wasPrice)
      ? { wasPrice: { amount: wasPrice.amount, currency: wasPrice.currency } }
      : {}),
    ...(isShowableMoney(wasRrp)
      ? { wasRrp: { amount: wasRrp.amount, currency: wasRrp.currency } }
      : {}),
    ...(typeof promotionEndDate === 'string' ? { promotionEndDate } : {}),
    ...(margin === null ? {} : { margin }),
    kind: kind as PriceKind,
    basis: basis as PriceBasis,
  };
}

/** A chunk failure worth asking again for: a 5xx, a network error or a malformed answer. A
 *  4xx is not one: the request itself is wrong, so a second try fails the same way. */
class RetryableChunkError extends Error {}

/** Fetch one chunk. A 403 answers every SKU in it unpriced (`ENTRY_FORBIDDEN`, PS-5).
 *  Throws on a transport failure, any other non-2xx or a body that is not an array; the
 *  caller asks once more after a {@link RetryableChunkError}, then turns the failure into
 *  "every SKU in the chunk is unread". */
async function fetchChunk(
  entryKey: string,
  customerAccountCode: string | null,
  chunk: readonly string[],
  signal: AbortSignal | undefined,
): Promise<PriceQuoteReadResult> {
  const url = withEntry(
    `${PRICE_QUOTES_PATH}?skus=${encodeURIComponent(chunk.join(','))}${accountParam(customerAccountCode)}`,
    entryKey,
  );
  let res: Response;
  try {
    res = await apiFetch(url, signal === undefined ? {} : { signal });
  } catch (error) {
    throw new RetryableChunkError(error instanceof Error ? error.message : 'Network error');
  }
  if (res.status === 403) {
    return {
      quotes: chunk.map((skuCode) => ({ skuCode, status: 'unpriced', reason: 'ENTRY_FORBIDDEN' })),
      unreadSkus: [],
    };
  }
  if (res.status >= 500) throw new RetryableChunkError(`Failed to load prices (${res.status})`);
  if (!res.ok) throw new Error(`Failed to load prices (${res.status})`);
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new RetryableChunkError('Malformed price quotes response');
  }
  if (!Array.isArray(body)) throw new RetryableChunkError('Malformed price quotes response');

  const asked = new Set(chunk);
  const answered = new Map<string, PriceQuoteDto>();
  for (const value of body) {
    const quote = parseQuote(value);
    // An entry for a SKU this chunk did not ask about, or a second one for the same SKU,
    // is ignored: the first answer per asked SKU is the one used.
    if (quote === null || !asked.has(quote.skuCode) || answered.has(quote.skuCode)) continue;
    answered.set(quote.skuCode, quote);
  }
  return {
    quotes: chunk.flatMap((sku) => answered.get(sku) ?? []),
    unreadSkus: chunk.filter((sku) => !answered.has(sku)),
  };
}

/**
 * Fetch the price quotes for `skuCodes` under the ordering entry `entryKey` (the session's
 * `activeEntryKey`, `<KIND>:<depotCode>`) for the trading account `customerAccountCode` (the
 * selected tile's account, the same account its cart uses, ADO-2248 DEC-10; `null` sends
 * none). Every chunk names the same account. Blank and duplicate SKUs are dropped, the rest are
 * chunked at {@link MAX_PRICE_QUOTE_BATCH_SIZE}, and each chunk succeeds or fails on its own.
 * A chunk that fails with a 5xx, a network error or a malformed answer is asked once more, for
 * all its SKUs together (ADO-2248 PS-4), unless the request was aborted; a 4xx is not. Never rejects: a chunk that fails twice (or is aborted) reports its
 * SKUs as unread.
 */
export async function fetchPriceQuotes(
  entryKey: string,
  customerAccountCode: string | null,
  skuCodes: readonly string[],
  signal?: AbortSignal,
): Promise<PriceQuoteReadResult> {
  const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
  if (unique.length === 0) return { quotes: [], unreadSkus: [] };

  const chunks: string[][] = [];
  for (let index = 0; index < unique.length; index += MAX_PRICE_QUOTE_BATCH_SIZE) {
    chunks.push(unique.slice(index, index + MAX_PRICE_QUOTE_BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk): Promise<PriceQuoteReadResult> => {
      for (let attempt = 1; ; attempt += 1) {
        try {
          return await fetchChunk(entryKey, customerAccountCode, chunk, signal);
        } catch (error) {
          // This chunk established nothing: its SKUs are unknown, not unpriced. Ask once more
          // when another try can help and the caller has not given up on it.
          const retryable = error instanceof RetryableChunkError && signal?.aborted !== true;
          if (attempt === 2 || !retryable) return { quotes: [], unreadSkus: chunk };
        }
      }
    }),
  );
  return {
    quotes: results.flatMap((result) => result.quotes),
    unreadSkus: results.flatMap((result) => result.unreadSkus),
  };
}
